/**
 * Copyright IBM Corp. 2021-2023
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from "react";
import { Image as ImageIcon } from "@carbon/icons-react";
import classNames from "classnames";
import PropTypes from "prop-types";
import TriImageSkeleton from "./TriImageSkeleton";
import { getImageUrl } from "./api/TriImageApi";

/**
 * A react component for displaying TRIRIGA images.
 */
export default class TriImage extends React.PureComponent {
  state = {
    loading: false,
    image: null,
  };

  static propTypes = {
    /**
     * Specify an optional className to be applied to the image.
     */
    className: PropTypes.string,
    /**
     * An element that will be used as a placeholder until the image has loaded.
     */
    placeholder: PropTypes.elementType,
    /**
     * Sets the size of the image. It can have one of the following values:
     *
     * - contain: Scales the image as large as possible within its container without cropping or stretching the image.
     * - cover: Scales the image as large as possible to fill the container, stretching the image if necessary. If the proportions of the image differ from the container element, it is cropped either vertically or horizontally so that no empty space remains.
     */
    sizing: PropTypes.string,
    /**
     * If true, it displays a thumbnail version of the image (a smaller pixel area).
     */
    thumbnail: PropTypes.bool,
    /**
     *  The URL of the image. It is the value of an image field returned by a data source.
     */
    value: PropTypes.string,
  };

  static defaultProps = {
    sizing: "cover",
    placeholder: (props) => <ImageIcon size={24} {...props} />,
    thumbnail: false,
  };

  render() {
    const {
      sizing,
      placeholder: Placeholder,
      className: customClassName,
    } = this.props;
    const { loading, image } = this.state;
    if (loading) {
      return (
        <div className={classNames("triImage__placeholder", customClassName)}>
          <TriImageSkeleton />
        </div>
      );
    }
    else if (image === null) {
      if (Placeholder != null) {
        return (
          <div className={classNames("triImage__placeholder", customClassName)}>
            <Placeholder />
          </div>
        );
      }
      else {
        return null;
      }
    }
    const imageStyle = {
      backgroundSize: sizing,
      backgroundImage: `url(${image.href})`,
    };

    return (
      <div
        className={classNames("triImage", customClassName)}
        style={imageStyle}
      />
    );
  }

  componentDidMount() {
    this.loadImage(this.props.value, this.props.thumbnail);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.value !== prevProps.value ||
      this.props.thumbnail !== prevProps.thumbnail
    ) {
      this.loadImage(this.props.value, this.props.thumbnail);
    }
  }

  loadImage(value, thumbnail) {
    if (value == null) {
      this.setState({ loading: false});
      return;
    }
    this.setState({ loading: true }, async () => {
      const image = new Image();
      const imageUrl = getImageUrl(value, thumbnail);
      image.src = imageUrl;
      try {
        await image.decode();
        this.setState({
          loading: false,
          image: imageUrl,
        });
      } catch (error) {
        this.setState({ loading: false });
      }
    });
  }
}
